import React, { useEffect, useState } from "react";
import { Save, Loader2, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import RichEditor from "../../components/RichEditor";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";

export default function AdminTerms() {
  const [form, setForm] = useState(null);
  const [lang, setLang] = useState("en");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/terms").then((r) => setForm(r.data)).catch(() => toast.error("Could not load Terms")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/terms", form);
      toast.success("Terms of Use saved");
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p>;

  const sfx = lang;
  return (
    <div data-testid="admin-terms">
      <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
        <h1 className="font-display text-4xl font-semibold text-primary flex items-center gap-2"><FileText size={24} className="text-secondary" /> Terms of Use</h1>
        <div className="flex items-center gap-3">
          <a href="/terms" target="_blank" rel="noreferrer" className="text-secondary font-semibold inline-flex items-center gap-1 text-sm">View page <ExternalLink size={13} /></a>
          <Button onClick={save} disabled={saving} data-testid="terms-save" className="rounded-full">
            {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground mb-6">Edit the public Terms of Use page. Content is bilingual — switch language to edit both; they save together.</p>

      <div className="flex rounded-full border border-primary/20 overflow-hidden w-max mb-6">
        {["en", "fr"].map((l) => (
          <button key={l} data-testid={`terms-lang-${l}`} onClick={() => setLang(l)} className={`px-5 py-2 text-sm font-semibold ${lang === l ? "bg-primary text-primary-foreground" : "text-primary"}`}>{l.toUpperCase()}</button>
        ))}
      </div>

      <div className="max-w-3xl space-y-5">
        <div>
          <Label>Page title</Label>
          <Input data-testid={`terms-title-${sfx}`} value={form[`title_${sfx}`] || ""} onChange={(e) => setForm({ ...form, [`title_${sfx}`]: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>Intro paragraph</Label>
          <Input data-testid={`terms-intro-${sfx}`} value={form[`intro_${sfx}`] || ""} onChange={(e) => setForm({ ...form, [`intro_${sfx}`]: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>Body</Label>
          <div className="mt-1.5">
            <RichEditor key={sfx} value={form[`body_${sfx}`] || ""} testid={`terms-body-${sfx}`}
              onChange={(html) => setForm((p) => ({ ...p, [`body_${sfx}`]: html }))} />
          </div>
        </div>
      </div>
    </div>
  );
}
